import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Fade,
  Grow,
  Paper,
  Divider,
  Stack,
  Tooltip,
  CircularProgress,
  Alert,
  Skeleton,
  alpha,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import SourceIcon from '@mui/icons-material/Source';
import DescriptionIcon from '@mui/icons-material/Description';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { canModifyRecord } from '../utils/permissions';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  overflow: 'hidden',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
  },
  '&:last-child': {
    marginBottom: 0
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'light'
    ? theme.palette.grey[50]
    : alpha(theme.palette.background.paper, 0.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  '& p': {
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.7,
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: theme.spacing(0.5),
  verticalAlign: 'middle',
  color: theme.palette.text.secondary,
}));

const IntelChip = styled(Chip)(({ theme }) => ({
  height: '24px',
  fontSize: '0.75rem',
  fontWeight: 500,
  borderRadius: '12px',
  backgroundColor: alpha(theme.palette.info.main, 0.1),
  color: theme.palette.info.main,
  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
  '& .MuiChip-icon': {
    fontSize: '16px',
  },
}));

// Helper functions
const getAvatarUrl = (userId) => {
  return `${getMediaBaseUrl()}/users/${userId}.png?${new Date().getTime()}`;
};

// Loading skeleton component
const IntelSkeleton = () => (
  <Card sx={{ mb: 2, borderRadius: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" width="30%" height={20} />
          <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
        </Box>
      </Box>
      <Skeleton variant="text" width="70%" height={16} />
    </CardContent>
  </Card>
);

const IntelTab = ({ playerId, onDataLoaded }) => {
  const { t } = useTranslation();
  const [intelData, setIntelData] = useState([]);
  const [openRows, setOpenRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentUser] = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));

  // Edit state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentIntel, setCurrentIntel] = useState(null);
  const [editedFecha, setEditedFecha] = useState('');
  const [editedFuente, setEditedFuente] = useState('');
  const [editedDetalles, setEditedDetalles] = useState('');

  // Delete state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [intelToDelete, setIntelToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  // Fetch intel data
  useEffect(() => {
    const fetchIntelData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get('/api/intel', {
          params: { playerId }
        });

        setIntelData(response.data);

        if (onDataLoaded) {
          onDataLoaded(response.data.length > 0);
        }

        // Auto-open first intel
        if (response.data.length > 0) {
          setOpenRows({ [response.data[0].id]: true });
        }
      } catch (error) {
        console.error('Error fetching intel data:', error);
        setError(t('playerCardCreator.intelTab.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    fetchIntelData();
  }, [playerId, backendUrl, section, onDataLoaded, t]);

  // Handlers
  const toggleRow = useCallback((rowId) => {
    setOpenRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  const handleEditClick = useCallback((intel) => {
    setCurrentIntel(intel);

    // Format date for input
    if (intel.fecha) {
      let dateObj = new Date(intel.fecha);
      dateObj = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000);
      const isoDate = dateObj.toISOString().split('T')[0];
      setEditedFecha(isoDate);
    } else {
      setEditedFecha('');
    }

    setEditedFuente(intel.fuente || '');
    setEditedDetalles(intel.detalles || '');
    setOpenEditDialog(true);
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!currentIntel) return;

    setSaving(true);
    const transformedDetalles = editedDetalles
      .replace(/<p><br><\/p>/g, '<br>')
      .replace(/<p>/g, '')
      .replace(/<\/p>/g, '<br>')
      .replace(/<br><br>/g, '<br>');

    const updateData = {
      fecha: editedFecha,
      fuente: editedFuente,
      detalles: transformedDetalles
    };

    try {
      await api.put(`/api/intel/${currentIntel.id}`, updateData);

      setIntelData((prevData) =>
        prevData.map((item) =>
          item.id === currentIntel.id
            ? { ...item, ...updateData, detalles: transformedDetalles }
            : item
        )
      );

      setOpenEditDialog(false);
    } catch (error) {
      console.error('Error updating intel:', error);
      setError(t('playerCardCreator.intelTab.errorUpdating'));
    } finally {
      setSaving(false);
    }
  }, [currentIntel, editedFecha, editedFuente, editedDetalles, backendUrl, section, t]);

  const handleCloseEditDialog = useCallback(() => {
    setOpenEditDialog(false);
    setCurrentIntel(null);
  }, []);

  const handleDeleteClick = useCallback((intel) => {
    setIntelToDelete(intel);
    setOpenDeleteDialog(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!intelToDelete) return;

    setDeleting(true);
    try {
      await api.delete(`/api/intel/${intelToDelete.id}`);

      setIntelData((prevData) =>
        prevData.filter((item) => item.id !== intelToDelete.id)
      );

      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting intel:', error);
      setError(t('playerCardCreator.intelTab.errorDeleting'));
    } finally {
      setDeleting(false);
    }
  }, [intelToDelete, backendUrl, section, t]);

  const cancelDelete = useCallback(() => {
    setOpenDeleteDialog(false);
    setIntelToDelete(null);
  }, []);

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <IntelSkeleton />
        <IntelSkeleton />
        <IntelSkeleton />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      </Box>
    );
  }

  // Render empty state
  if (!intelData.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <InfoOutlinedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {t('playerCardCreator.intelTab.noDataTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('playerCardCreator.intelTab.noDataDescription')}
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        {intelData.map((row, index) => (
          <Grow in timeout={300} key={row.id} style={{ transitionDelay: `${index * 50}ms` }}>
            <StyledCard>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <HeaderBox onClick={() => toggleRow(row.id)}>
                  {/* Left section with avatar and info */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      flexGrow: 1,
                      minWidth: 0,
                      gap: 2,
                    }}
                  >
                    <Avatar
                      src={getAvatarUrl(row.usuario_id)}
                      alt={row.usuario_nombre}
                      sx={{
                        width: 48,
                        height: 48,
                        border: `2px solid ${theme.palette.divider}`,
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                      }}
                    />

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      {/* User name and date */}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="600">
                          {row.usuario_nombre}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <IconWrapper>
                            <CalendarTodayIcon sx={{ fontSize: 14 }} />
                          </IconWrapper>
                          {new Date(row.fecha).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </Typography>
                      </Box>

                      {/* Source with icon */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <IconWrapper>
                            <SourceIcon sx={{ fontSize: 16 }} />
                          </IconWrapper>
                          {t('playerCardCreator.intelTab.source')}: {row.fuente}
                        </Typography>
                      </Box>

                    </Box>
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: { xs: 2, sm: 0 } }}>
                    {canModifyRecord(currentUser, row.usuario_id) && (
                      <>
                        <Tooltip title={t('playerCardCreator.intelTab.editIntel')}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(row);
                            }}
                            size="small"
                            sx={{
                              color: 'primary.main',
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t('playerCardCreator.intelTab.deleteIntel')}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(row);
                            }}
                            size="small"
                            sx={{
                              color: 'error.main',
                              '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24 }} />

                    <Tooltip title={openRows[row.id] ? t('playerCardCreator.intelTab.collapse') : t('playerCardCreator.intelTab.expand')}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRow(row.id);
                        }}
                        size="small"
                        sx={{
                          transform: openRows[row.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </HeaderBox>

                {/* Collapsible content */}
                <Collapse in={openRows[row.id]} timeout="auto" unmountOnExit>
                  <ContentBox>
                    <Typography
                      variant="body1"
                      component="div"
                      dangerouslySetInnerHTML={{ __html: row.detalles }}
                      sx={{
                        '& p': { marginBottom: 1.5 },
                        '& br': { display: 'block', content: '""', marginTop: 0.5 },
                      }}
                    />
                  </ContentBox>
                </Collapse>
              </CardContent>
            </StyledCard>
          </Grow>
        ))}

        {/* Edit Dialog */}
        <StyledDialog
          open={openEditDialog}
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            handleCloseEditDialog();
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ mr: 1, color: 'info.main' }} />
              <Typography variant="h6">{t('playerCardCreator.intelTab.editDialogTitle')}</Typography>
            </Box>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 3 }}>
            {currentIntel && (
              <Stack spacing={3}>
                {/* Date field */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    <CalendarTodayIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    {t('playerCardCreator.intelTab.date')}
                  </Typography>
                  <TextField
                    type="date"
                    fullWidth
                    value={editedFecha}
                    onChange={(e) => setEditedFecha(e.target.value)}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                {/* Source field */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    <SourceIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    {t('playerCardCreator.intelTab.sourceLabel')}
                  </Typography>
                  <TextField
                    fullWidth
                    value={editedFuente}
                    onChange={(e) => setEditedFuente(e.target.value)}
                    placeholder={t('playerCardCreator.intelTab.sourcePlaceholder')}
                    variant="outlined"
                  />
                </Box>

                {/* Details editor */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    <DescriptionIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    {t('playerCardCreator.intelTab.details')}
                  </Typography>
                  <Paper variant="outlined" sx={{ borderRadius: 1 }}>
                    <ReactQuill
                      theme="snow"
                      value={editedDetalles}
                      onChange={setEditedDetalles}
                      placeholder={t('playerCardCreator.intelTab.detailsPlaceholder')}
                      style={{ minHeight: '200px' }}
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          ['blockquote', 'code-block'],
                          ['link'],
                          ['clean']
                        ],
                      }}
                    />
                  </Paper>
                </Box>
              </Stack>
            )}
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={handleCloseEditDialog}
              color="inherit"
              disabled={saving}
            >
              {t('playerCardCreator.intelTab.cancel')}
            </Button>
            <Button
              onClick={handleEditSave}
              variant="contained"
              disabled={saving}
              startIcon={saving && <CircularProgress size={16} />}
            >
              {saving ? t('playerCardCreator.intelTab.saving') : t('playerCardCreator.intelTab.saveChanges')}
            </Button>
          </DialogActions>
        </StyledDialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={cancelDelete}
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DeleteIcon sx={{ mr: 1, color: 'error.main' }} />
              <Typography variant="h6">{t('playerCardCreator.intelTab.deleteDialogTitle')}</Typography>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
              {t('playerCardCreator.intelTab.deleteWarning')}
            </Alert>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={cancelDelete}
              color="inherit"
              disabled={deleting}
            >
              {t('playerCardCreator.intelTab.cancel')}
            </Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting && <CircularProgress size={16} color="inherit" />}
            >
              {deleting ? t('playerCardCreator.intelTab.deleting') : t('playerCardCreator.intelTab.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default IntelTab;